import React from "react";
import { Sidebar, Menu, MenuItem, SubMenu, sidebarClasses } from "react-pro-sidebar";
import { Link } from "react-router-dom";
import { IoClose } from "react-icons/io5";
import { FaSignOutAlt, FaUserShield, FaChevronUp, FaChevronRight, FaSearch } from "react-icons/fa";
import { ThemeToggle, Button } from "../components/UI";

// - Инструменты раздела «Прайс-листы» (загрузчик и парсинг) — всегда в конце раздела
const PRICE_TOOL_PATHS = ["/price-list-recognition", "/parser"];

/**
 * Левый сайдбар приложения (навигация + профиль). Вынесен из App.js.
 * Данные и обработчики приходят пропсами из App.
 */
export default function AppSidebar({
  isSidebarVisible,
  toggleSidebar,
  activeSubMenu,
  handleSubMenuClick,
  currentSection,
  role,
  isManager,
  filteredMenuItems,
  referenceInfoTabs,
  warehouseTabs,
  deliveryTabs,
  analogSettingsTabs,
  priceLists,
  location,
  isUserMenuOpen,
  setIsUserMenuOpen,
  isAdminMenuOpen,
  setIsAdminMenuOpen,
  userMenuRef,
  visibleProfileMenuItems,
  visibleAdminProfileItems,
  handleProfileNavigation,
  handleLogout,
  setPaletteOpen,
  avatarUrl,
  userDisplayName,
  userInitials,
}) {
  return (
          <Sidebar
            width={isSidebarVisible ? "300px" : "60px"}
            collapsedWidth="60px"
            rootStyles={{
              // react-pro-sidebar красит правую границу корня в #efefef — в тёмной теме это белая полоса.
              borderColor: "var(--ui-border)",
              [`.${sidebarClasses.container}`]: {
                background: "var(--ui-surface)",
                borderRight: "1px solid var(--ui-border)",
                position: "fixed",
                top: 0,
                left: "10px",
                height: "100vh",
                marginTop: "5px",
                marginLeft: "0",
                boxShadow: "var(--ui-shadow-md)",
                borderRadius: "0 12px 12px 0",
                boxSizing: "border-box",
                maxWidth: "calc(100vw - 10px)",
                overflowX: "hidden",
                overflowY: "auto",
                transition: "width 0.3s ease, margin 0.3s ease",
                visibility: isSidebarVisible ? "visible" : "hidden",
                minHeight: "100vh",
                width: isSidebarVisible ? "300px" : "60px",
                zIndex: 100,
              },
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                minHeight: "100vh",
                width: "100%",
                overflowX: "hidden",
              }}
            >
              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  overflowX: "hidden",
                  paddingBottom: "16px",
                }}
              >
                <Menu
                  menuItemStyles={{
                    button: ({ level, active }) => ({
                      background: active ? "var(--ui-primary)" : "transparent",
                      color: active ? "var(--ui-primary-contrast)" : "var(--ui-text)",
                      margin: "4px 8px",
                      borderRadius: "8px",
                      padding: level === 1 ? "10px 16px" : "8px 24px",
                      maxWidth: "100%",
                      boxSizing: "border-box",
                      transition:
                        "background 0.2s ease, color 0.2s ease, transform 0.2s ease",
                      "&:hover": {
                        background: "var(--ui-primary-soft)",
                        color: "var(--ui-primary)",
                        transform: "translateX(4px)",
                      },
                    }),
                    label: {
                      fontSize: "15px",
                      fontWeight: "400",
                      whiteSpace: isSidebarVisible ? "normal" : "nowrap",
                      overflow: isSidebarVisible ? "visible" : "hidden",
                    },
                    icon: {
                      fontSize: "18px",
                      marginRight: isSidebarVisible ? "12px" : "0",
                      color: "inherit",
                    },
                    subMenuContent: {
                      background: "transparent",
                      borderRadius: "8px",
                      boxShadow: "none",
                      margin: "4px 8px",
                      padding: "4px 0",
                      maxWidth: "100%",
                      overflowX: "hidden",
                    },
                    SubMenuExpandIcon: {
                      color: "var(--ui-primary)",
                      fontSize: "12px",
                    },
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "center",
                      padding: "12px 16px",
                      background: "var(--ui-primary)",
                      color: "var(--ui-primary-contrast)",
                      fontWeight: "500",
                      fontSize: "16px",
                      borderTopLeftRadius: "12px",
                      borderTopRightRadius: "12px",
                    }}
                  >
                    <button
                      onClick={toggleSidebar}
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "var(--ui-primary-contrast)",
                        fontSize: "30px",
                        cursor: "pointer",
                        display: isSidebarVisible ? "block" : "none",
                      }}
                    >
                      <IoClose />
                    </button>
                  </div>

                  {filteredMenuItems.map((item) =>
                    item.hasDropdown ? (
                      <SubMenu
                        key={item.section}
                        label={item.name}
                        icon={item.icon}
                        open={activeSubMenu === item.section}
                        onClick={() => handleSubMenuClick(item.section)}
                        rootStyles={{
                          background:
                            currentSection === item.section
                              ? "var(--ui-primary-soft)"
                              : "transparent",
                          color:
                            currentSection === item.section
                              ? "var(--ui-primary)"
                              : "var(--ui-text)",
                          borderRadius: "8px",
                          margin: "4px 8px",
                          padding: "10px 16px",
                          transition: "background 0.2s ease, color 0.2s ease",
                          "& .ps-menu-label": {
                            fontSize: "15px",
                            fontWeight: "400",
                          },
                          "& .ps-icon": {
                            fontSize: "18px",
                          },
                          ["&.ps-open"]: {
                            background: "var(--ui-primary-soft)",
                            color: "var(--ui-primary)",
                          },
                          ["& .ps-submenu-content"]: {
                            background: "var(--ui-surface-alt) !important",
                            borderRadius: "8px",
                            padding: "4px 0",
                          },
                        }}
                      >
                        {item.section === "reference-info" ? (
                          role === "god" ? (
                            <>
                              <SubMenu
                                label="Используемые"
                                defaultOpen={true}
                                rootStyles={{
                                  "& .ps-menu-label": {
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    paddingLeft: "16px",
                                  },
                                  "& .ps-submenu-content": {
                                    background: "transparent !important",
                                  },
                                }}
                              >
                                {referenceInfoTabs
                                  .filter((tab) =>
                                    [
                                      "guide",
                                      "production",
                                      "nomenclature_images",
                                      "important_groups",
                                      "all_groups",
                                      "bitrix_elements",
                                      "talan",
                                    ].includes(tab.section),
                                  )
                                  .map((tab) => (
                                    <MenuItem
                                      key={tab.path}
                                      component={<Link to={tab.path} />}
                                      active={location.pathname === tab.path}
                                      style={{ paddingLeft: "48px" }}
                                    >
                                      {tab.name}
                                    </MenuItem>
                                  ))}
                              </SubMenu>
                              <SubMenu
                                label="Не используемые"
                                rootStyles={{
                                  "& .ps-menu-label": {
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    paddingLeft: "16px",
                                  },
                                  "& .ps-submenu-content": {
                                    background: "transparent !important",
                                  },
                                }}
                              >
                                {referenceInfoTabs
                                  .filter(
                                    (tab) =>
                                      ![
                                        "guide",
                                        "production",
                                        "nomenclature_images",
                                        "important_groups",
                                        "all_groups",
                                        "bitrix_elements",
                                        "talan",
                                      ].includes(tab.section),
                                  )
                                  .map((tab) => (
                                    <MenuItem
                                      key={tab.path}
                                      component={<Link to={tab.path} />}
                                      active={location.pathname === tab.path}
                                      style={{ paddingLeft: "48px" }}
                                    >
                                      {tab.name}
                                    </MenuItem>
                                  ))}
                              </SubMenu>
                            </>
                          ) : (
                            referenceInfoTabs.map((tab) => (
                              <MenuItem
                                key={tab.path}
                                component={<Link to={tab.path} />}
                                active={location.pathname === tab.path}
                                style={{ paddingLeft: "32px" }}
                              >
                                {tab.name}
                              </MenuItem>
                            ))
                          )
                        ) : item.section === "warehouses" ? (
                          role === "god" || isManager ? (
                            <>
                              {warehouseTabs
                                .filter(
                                  (tab) =>
                                    tab.name === "Общий склад" ||
                                    tab.name === "Общий склад актуальный",
                                )
                                .map((tab) => (
                                  <MenuItem
                                    key={tab.path}
                                    component={<Link to={tab.path} />}
                                    active={location.pathname === tab.path}
                                    style={{ paddingLeft: "32px" }}
                                  >
                                    {tab.name}
                                  </MenuItem>
                                ))}
                              <SubMenu
                                label="СПБ"
                                rootStyles={{
                                  "& .ps-menu-label": {
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    paddingLeft: "16px",
                                    whiteSpace: "nowrap",
                                  },
                                  "& .ps-submenu-content": {
                                    background: "transparent !important",
                                    minWidth: 0,
                                    maxWidth: "100%",
                                    overflowX: "hidden",
                                  },
                                  "& .ps-menu-button": {
                                    whiteSpace: "normal",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  },
                                }}
                              >
                                {warehouseTabs
                                  .filter(
                                    (tab) =>
                                      tab.path.includes("price_list_") &&
                                      (priceLists.some(
                                        (pl) =>
                                          pl.id ===
                                            parseInt(
                                              tab.path.split("_").pop(),
                                            ) && pl.region === "СПБ",
                                      ) ||
                                        tab.path ===
                                          "/warehouses/price_list_decke_spb" ||
                                        tab.path ===
                                          "/warehouses/price_list_nerud_spb"),
                                  )
                                  .map((tab) => (
                                    <MenuItem
                                      key={tab.path}
                                      component={<Link to={tab.path} />}
                                      active={location.pathname === tab.path}
                                      style={{ paddingLeft: "48px" }}
                                    >
                                      {tab.name}
                                    </MenuItem>
                                  ))}
                                {warehouseTabs
                                  .filter(
                                    (tab) =>
                                      tab.name === "Склад СПБ" ||
                                      tab.name === "Акционные товары СПБ" ||
                                      tab.name === "Склад Мурино",
                                  )
                                  .map((tab) => (
                                    <MenuItem
                                      key={tab.path}
                                      component={<Link to={tab.path} />}
                                      active={location.pathname === tab.path}
                                      style={{ paddingLeft: "48px" }}
                                    >
                                      {tab.name}
                                    </MenuItem>
                                  ))}
                                {/* Прайс Grandline */}
                                {warehouseTabs
                                  .filter(
                                    (tab) => tab.name === "Прайс Grandline",
                                  )
                                  .map((tab) => (
                                    <MenuItem
                                      key={tab.path}
                                      component={<Link to={tab.path} />}
                                      active={location.pathname === tab.path}
                                      style={{ paddingLeft: "48px" }}
                                    >
                                      {tab.name}
                                    </MenuItem>
                                  ))}
                                {/* Прайс TopHouse */}
                                {warehouseTabs
                                  .filter(
                                    (tab) => tab.name === "Прайс TopHouse",
                                  )
                                  .map((tab) => (
                                    <MenuItem
                                      key={tab.path}
                                      component={<Link to={tab.path} />}
                                      active={location.pathname === tab.path}
                                      style={{ paddingLeft: "48px" }}
                                    >
                                      {tab.name}
                                    </MenuItem>
                                  ))}
                                {/* ADM-68: ТОП Номенклатур — этот сайдбар рендерит вкладки складов
                                    не по tab.region, а поштучно захардкоженными фильтрами (см. блоки
                                    выше); tab.region="СПБ" в navConfig.js одного добавления в список
                                    не хватало — сюда её никто не подключал вовсе */}
                                {warehouseTabs
                                  .filter(
                                    (tab) => tab.name === "ТОП Номенклатур",
                                  )
                                  .map((tab) => (
                                    <MenuItem
                                      key={tab.path}
                                      component={<Link to={tab.path} />}
                                      active={location.pathname === tab.path}
                                      style={{ paddingLeft: "48px" }}
                                    >
                                      {tab.name}
                                    </MenuItem>
                                  ))}
                                {warehouseTabs
                                  .filter(
                                    (tab) =>
                                      tab.path ===
                                      "/warehouses/technonicol_spb",
                                  )
                                  .map((tab) => (
                                    <MenuItem
                                      key={tab.path}
                                      component={<Link to={tab.path} />}
                                      active={location.pathname === tab.path}
                                      style={{ paddingLeft: "48px" }}
                                    >
                                      {tab.name}
                                    </MenuItem>
                                  ))}
                                {warehouseTabs
                                  .filter(
                                    (tab) =>
                                      tab.path === "/warehouses/izolife_spb",
                                  )
                                  .map((tab) => (
                                    <MenuItem
                                      key={tab.path}
                                      component={<Link to={tab.path} />}
                                      active={location.pathname === tab.path}
                                      style={{ paddingLeft: "48px" }}
                                    >
                                      {tab.name}
                                    </MenuItem>
                                  ))}
                              </SubMenu>
                              <SubMenu
                                label="МСК"
                                rootStyles={{
                                  "& .ps-menu-label": {
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    paddingLeft: "16px",
                                  },
                                  "& .ps-submenu-content": {
                                    background: "transparent !important",
                                  },
                                }}
                              >
                                {warehouseTabs
                                  .filter(
                                    (tab) =>
                                      tab.path.includes("price_list_") &&
                                      (priceLists.some(
                                        (pl) =>
                                          pl.id ===
                                            parseInt(
                                              tab.path.split("_").pop(),
                                            ) && pl.region === "МСК",
                                      ) ||
                                        tab.path ===
                                          "/warehouses/price_list_beton_msk" ||
                                        tab.path ===
                                          "/warehouses/price_list_decke_msk" ||
                                        tab.path ===
                                          "/warehouses/price_list_nerud_msk"),
                                  )
                                  .map((tab) => (
                                    <MenuItem
                                      key={tab.path}
                                      component={<Link to={tab.path} />}
                                      active={location.pathname === tab.path}
                                      style={{ paddingLeft: "48px" }}
                                    >
                                      {tab.name}
                                    </MenuItem>
                                  ))}
                                {warehouseTabs
                                  .filter(
                                    (tab) =>
                                      tab.name === "Акционные товары МСК",
                                  )
                                  .map((tab) => (
                                    <MenuItem
                                      key={tab.path}
                                      component={<Link to={tab.path} />}
                                      active={location.pathname === tab.path}
                                      style={{ paddingLeft: "48px" }}
                                    >
                                      {tab.name}
                                    </MenuItem>
                                  ))}
                                {warehouseTabs
                                  .filter(
                                    (tab) =>
                                      tab.path === "/warehouses/izolife_msk",
                                  )
                                  .map((tab) => (
                                    <MenuItem
                                      key={tab.path}
                                      component={<Link to={tab.path} />}
                                      active={location.pathname === tab.path}
                                      style={{ paddingLeft: "48px" }}
                                    >
                                      {tab.name}
                                    </MenuItem>
                                  ))}
                              </SubMenu>
                              <SubMenu
                                label="КРД"
                                rootStyles={{
                                  "& .ps-menu-label": {
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    paddingLeft: "16px",
                                  },
                                  "& .ps-submenu-content": {
                                    background: "transparent !important",
                                  },
                                }}
                              >
                                {warehouseTabs
                                  .filter(
                                    (tab) =>
                                      tab.path.includes("price_list_") &&
                                      priceLists.some(
                                        (pl) =>
                                          pl.id ===
                                            parseInt(
                                              tab.path.split("_").pop(),
                                            ) && pl.region === "КРД",
                                      ),
                                  )
                                  .map((tab) => (
                                    <MenuItem
                                      key={tab.path}
                                      component={<Link to={tab.path} />}
                                      active={location.pathname === tab.path}
                                      style={{ paddingLeft: "48px" }}
                                    >
                                      {tab.name}
                                    </MenuItem>
                                  ))}
                                {warehouseTabs
                                  .filter(
                                    (tab) =>
                                      tab.name === "Акционные товары КРД",
                                  )
                                  .map((tab) => (
                                    <MenuItem
                                      key={tab.path}
                                      component={<Link to={tab.path} />}
                                      active={location.pathname === tab.path}
                                      style={{ paddingLeft: "48px" }}
                                    >
                                      {tab.name}
                                    </MenuItem>
                                  ))}
                                {warehouseTabs
                                  .filter(
                                    (tab) =>
                                      tab.path === "/warehouses/izolife_krd",
                                  )
                                  .map((tab) => (
                                    <MenuItem
                                      key={tab.path}
                                      component={<Link to={tab.path} />}
                                      active={location.pathname === tab.path}
                                      style={{ paddingLeft: "48px" }}
                                    >
                                      {tab.name}
                                    </MenuItem>
                                  ))}
                              </SubMenu>
                              <SubMenu
                                label="Остальное"
                                rootStyles={{
                                  "& .ps-menu-label": {
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    paddingLeft: "16px",
                                  },
                                  "& .ps-submenu-content": {
                                    background: "transparent !important",
                                  },
                                }}
                              >
                                {warehouseTabs
                                  .filter(
                                    (tab) =>
                                      !tab.name.includes("Общий склад") &&
                                      !tab.path.includes("price_list_") &&
                                      !PRICE_TOOL_PATHS.includes(tab.path) &&
                                      tab.name !== "Склад СПБ" &&
                                      tab.name !== "Акционные товары" &&
                                      tab.name !== "Склад Мурино",
                                  )
                                  .map((tab) => (
                                    <MenuItem
                                      key={tab.path}
                                      component={<Link to={tab.path} />}
                                      active={location.pathname === tab.path}
                                      style={{ paddingLeft: "48px" }}
                                    >
                                      {tab.name}
                                    </MenuItem>
                                  ))}
                                {warehouseTabs
                                  .filter(
                                    (tab) =>
                                      tab.path.includes("price_list_") &&
                                      priceLists.some(
                                        (pl) =>
                                          pl.id ===
                                            parseInt(
                                              tab.path.split("_").pop(),
                                            ) &&
                                          pl.region !== "СПБ" &&
                                          pl.region !== "МСК" &&
                                          pl.region !== "КРД",
                                      ),
                                  )
                                  .map((tab) => (
                                    <MenuItem
                                      key={tab.path}
                                      component={<Link to={tab.path} />}
                                      active={location.pathname === tab.path}
                                      style={{ paddingLeft: "48px" }}
                                    >
                                      {tab.name}
                                    </MenuItem>
                                  ))}
                              </SubMenu>
                              {/* - Загрузчик прайсов и парсинг отрисованы явно и последними: у god
                                    и менеджера остальные вкладки разложены по региональным
                                    подменю, и без этого они ушли бы в «Остальное» */}
                              {warehouseTabs
                                .filter((tab) => PRICE_TOOL_PATHS.includes(tab.path))
                                .map((tab) => (
                                  <MenuItem
                                    key={tab.path}
                                    component={<Link to={tab.path} />}
                                    active={location.pathname === tab.path}
                                    style={{ paddingLeft: "32px" }}
                                  >
                                    {tab.name}
                                  </MenuItem>
                                ))}
                            </>
                          ) : (
                            warehouseTabs.map((tab) => (
                              <MenuItem
                                key={tab.path}
                                component={<Link to={tab.path} />}
                                active={location.pathname === tab.path}
                                style={{ paddingLeft: "32px" }}
                              >
                                {tab.name}
                              </MenuItem>
                            ))
                          )
                        ) : (
                          (item.section === "delivery"
                            ? deliveryTabs
                            : item.section === "analog-settings"
                              ? analogSettingsTabs
                              : referenceInfoTabs
                          ).map((tab) => (
                            <MenuItem
                              key={tab.path}
                              component={<Link to={tab.path} />}
                              active={location.pathname === tab.path}
                              style={{ paddingLeft: "32px" }}
                            >
                              {tab.name}
                            </MenuItem>
                          ))
                        )}
                      </SubMenu>
                    ) : (
                      <MenuItem
                        key={item.section}
                        icon={item.icon}
                        component={
                          item.section !== "login" ? (
                            <Link to={item.path} />
                          ) : undefined
                        }
                        onClick={item.onClick}
                        active={currentSection === item.section}
                        style={
                          item.section === "login" ? { color: "var(--ui-danger)" } : {}
                        }
                      >
                        {item.name}
                      </MenuItem>
                    ),
                  )}
                </Menu>
              </div>

              <div
                ref={userMenuRef}
                style={{
                  position: "sticky",
                  bottom: 0,
                  padding: "12px",
                  background: "var(--ui-surface)",
                  borderTop: "1px solid var(--ui-border)",
                  backdropFilter: "blur(10px)",
                  zIndex: 2,
                }}
              >
                {isUserMenuOpen && (
                  <div
                    style={{
                      position: "absolute",
                      left: "12px",
                      right: "12px",
                      bottom: "calc(100% - 4px)",
                      background: "var(--ui-surface)",
                      border: "1px solid var(--ui-border)",
                      borderRadius: "18px",
                      boxShadow: "var(--ui-shadow-lg)",
                      padding: "10px",
                      animation: "dropdownSlide 0.2s ease",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                      }}
                    >
                      {visibleProfileMenuItems.map((item) => {
                        const itemStyle = {
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          width: "100%",
                          border: "none",
                          borderRadius: "12px",
                          background:
                            location.pathname === item.path
                              ? "var(--ui-primary-soft)"
                              : "transparent",
                          color:
                            location.pathname === item.path
                              ? "var(--ui-primary)"
                              : "var(--ui-ink)",
                          padding: "11px 12px",
                          fontSize: "14px",
                          textAlign: "left",
                          cursor: "pointer",
                        };
                        const itemBody = (
                          <>
                            <span style={{ fontSize: "15px" }}>{item.icon}</span>
                            <span>{item.name}</span>
                          </>
                        );

                        // Внешний сервис («Номенклатура (Клио)») — не роут
                        // админки, а ссылка наружу в новую вкладку. Через
                        // handleProfileNavigation её пускать нельзя: там
                        // navigate(), роутер увёл бы на несуществующий путь.
                        // Меню закрываем руками — этим занимался тот же хелпер.
                        if (item.external) {
                          return (
                            <a
                              key={item.url}
                              href={item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => {
                                setIsUserMenuOpen(false);
                                setIsAdminMenuOpen(false);
                              }}
                              style={{ ...itemStyle, textDecoration: "none" }}
                            >
                              {itemBody}
                            </a>
                          );
                        }

                        return (
                          <button
                            key={item.path}
                            type="button"
                            onClick={() => handleProfileNavigation(item.path)}
                            style={itemStyle}
                          >
                            {itemBody}
                          </button>
                        );
                      })}

                      {visibleAdminProfileItems.length > 0 && (
                        <div
                          style={{
                            borderTop: "1px solid var(--ui-border)",
                            marginTop: "4px",
                            paddingTop: "6px",
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => setIsAdminMenuOpen((prev) => !prev)}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              width: "100%",
                              border: "none",
                              borderRadius: "12px",
                              background: isAdminMenuOpen
                                ? "var(--ui-primary-soft)"
                                : "transparent",
                              color: "var(--ui-ink)",
                              padding: "11px 12px",
                              fontSize: "14px",
                              textAlign: "left",
                              cursor: "pointer",
                            }}
                          >
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                              }}
                            >
                              <FaUserShield />
                              <span>Администрирование</span>
                            </span>
                            {isAdminMenuOpen ? (
                              <FaChevronUp size={12} />
                            ) : (
                              <FaChevronRight size={12} />
                            )}
                          </button>

                          {isAdminMenuOpen && (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "6px",
                                marginTop: "6px",
                                paddingLeft: "10px",
                              }}
                            >
                              {visibleAdminProfileItems.map((item) => (
                                <button
                                  key={item.path}
                                  type="button"
                                  onClick={() =>
                                    handleProfileNavigation(item.path)
                                  }
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                    width: "100%",
                                    border: "none",
                                    borderRadius: "12px",
                                    background:
                                      location.pathname === item.path
                                        ? "var(--ui-primary-soft)"
                                        : "var(--ui-surface-alt)",
                                    color:
                                      location.pathname === item.path
                                        ? "var(--ui-primary)"
                                        : "var(--ui-ink)",
                                    padding: "10px 12px",
                                    fontSize: "14px",
                                    textAlign: "left",
                                    cursor: "pointer",
                                  }}
                                >
                                  <span style={{ fontSize: "15px" }}>
                                    {item.icon}
                                  </span>
                                  <span>{item.name}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      <div
                        style={{
                          borderTop: "1px solid var(--ui-border)",
                          marginTop: "4px",
                          paddingTop: "6px",
                        }}
                      >
                        <button
                          type="button"
                          onClick={handleLogout}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            width: "100%",
                            border: "none",
                            borderRadius: "12px",
                            background: "var(--ui-danger-soft)",
                            color: "var(--ui-danger)",
                            padding: "11px 12px",
                            fontSize: "14px",
                            textAlign: "left",
                            cursor: "pointer",
                          }}
                        >
                          <FaSignOutAlt />
                          <span>Выйти</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ marginBottom: "8px" }}>
                  <Button
                    variant="secondary"
                    fullWidth
                    icon={<FaSearch />}
                    onClick={() => setPaletteOpen(true)}
                    title="Поиск по вкладкам (Ctrl+K)"
                  >
                    Поиск вкладок
                  </Button>
                </div>
                <div style={{ marginBottom: "8px" }}>
                  <ThemeToggle withLabel variant="secondary" fullWidth />
                </div>

                <button
                  type="button"
                  onClick={() => setIsUserMenuOpen((prev) => !prev)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    width: "100%",
                    border: "1px solid var(--ui-border)",
                    borderRadius: "18px",
                    background: isUserMenuOpen
                      ? "linear-gradient(135deg, var(--ui-primary-soft) 0%, var(--ui-surface) 100%)"
                      : "var(--ui-surface)",
                    padding: "12px",
                    boxShadow: isUserMenuOpen
                      ? "var(--ui-shadow-lg)"
                      : "var(--ui-shadow-md)",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt={userDisplayName}
                      style={{
                        width: "46px",
                        height: "46px",
                        borderRadius: "14px",
                        objectFit: "cover",
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: "46px",
                        height: "46px",
                        borderRadius: "14px",
                        background:
                          "linear-gradient(135deg, var(--ui-primary) 0%, var(--ui-primary-hover) 100%)",
                        color: "var(--ui-primary-contrast)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "16px",
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {userInitials}
                    </div>
                  )}

                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        color: "var(--ui-ink)",
                        fontSize: "14px",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {userDisplayName}
                    </div>
                    <div
                      style={{
                        color: "var(--ui-text-muted)",
                        fontSize: "12px",
                        marginTop: "2px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {role || "Без роли"}
                    </div>
                  </div>

                  <div
                    style={{
                      color: "var(--ui-text-muted)",
                      fontSize: "12px",
                      flexShrink: 0,
                    }}
                  >
                    {isUserMenuOpen ? <FaChevronUp /> : <FaChevronRight />}
                  </div>
                </button>
              </div>
            </div>
          </Sidebar>
  );
}
